export const BADGE_SHEET_CELL_SIZE = 125;

export type Badge = {
    badge_code: string;
    description: string;
    unlocked_at: string;
};

export type BadgeDescription = {
    title: string;
    standard_description: string;
    additional_description?: (userCustomData: string) => string;
};

export type BadgeIconPosition = {
    iconIndex: number;
    iconColumn: number;
};

export type SingleBadgeDescription = {
    code: string;
    iconIndex: number;
} & BadgeDescription;

export type TieredBadgeDescription = {
    code: string;
    iconIndex: number;
    tiers: [string, BadgeDescription][];
    additional_description?: (userCustomData: string) => string;
};

export const tieredBadges: TieredBadgeDescription[] = [
    {
        code: 'classic.hardest_level',
        iconIndex: 0,
        additional_description: (userCustomData: string) =>
            `Awarded for completing ${userCustomData}.`,
        tiers: [
            [
                '1000',
                {
                    title: 'Pride and Joy',
                    standard_description:
                        'Complete a top 1000 classic extreme demon.',
                },
            ],
            [
                '750',
                {
                    title: 'Prove Yourself',
                    standard_description:
                        'Complete a top 750 classic extreme demon.',
                },
            ],
            [
                '500',
                {
                    title: 'Keep Going',
                    standard_description:
                        'Complete a top 500 classic extreme demon.',
                },
            ],
            [
                '250',
                {
                    title: 'Moving Forward',
                    standard_description:
                        'Complete a top 250 classic extreme demon.',
                },
            ],
            [
                '150',
                {
                    title: 'A New Beginning',
                    standard_description:
                        'Complete a top 150 classic extreme demon.',
                },
            ],
            [
                '100',
                {
                    title: 'Closing In',
                    standard_description:
                        'Complete a top 100 classic extreme demon.',
                },
            ],
            [
                '75',
                {
                    title: 'This Is Your Destiny',
                    standard_description:
                        'Complete a top 75 classic extreme demon.',
                },
            ],
            [
                '50',
                {
                    title: "Don't Look Down",
                    standard_description:
                        'Complete a top 50 classic extreme demon.',
                },
            ],
            [
                '25',
                {
                    title: 'Reach For The Summit',
                    standard_description:
                        'Complete a top 25 classic extreme demon.',
                },
            ],
            [
                '10',
                {
                    title: 'Mission Accomplished',
                    standard_description:
                        'Complete a top 10 classic extreme demon.',
                },
            ],
        ],
    },
    {
        code: 'platformer.hardest_level',
        iconIndex: 1,
        additional_description: (userCustomData: string) =>
            `Awarded for completing ${userCustomData}.`,
        tiers: [
            [
                '150',
                {
                    title: 'Jet Lagged',
                    standard_description:
                        'Complete a top 150 platformer extreme demon.',
                },
            ],
            [
                '125',
                {
                    title: 'IWillBeTheOne',
                    standard_description:
                        'Complete a top 125 platformer extreme demon.',
                },
            ],
            [
                '100',
                {
                    title: 'Into The Abyss',
                    standard_description:
                        'Complete a top 100 platformer extreme demon.',
                },
            ],
            [
                '75',
                {
                    title: 'Free Solo',
                    standard_description:
                        'Complete a top 75 platformer extreme demon.',
                },
            ],
            [
                '50',
                {
                    title: 'In The Dead Of Night',
                    standard_description:
                        'Complete a top 50 platformer extreme demon.',
                },
            ],
            [
                '25',
                {
                    title: 'Convoluted',
                    standard_description:
                        'Complete a top 25 platformer extreme demon.',
                },
            ],
            [
                '10',
                {
                    title: 'The Final Checkpoint',
                    standard_description:
                        'Complete a top 10 platformer extreme demon.',
                },
            ],
        ],
    },
    {
        code: 'global.level_completion',
        iconIndex: 2,
        tiers: [
            [
                '5',
                {
                    title: 'Extreme Beginner',
                    standard_description:
                        'Complete 5 extreme demons on the classic or platformer list.',
                },
            ],
            [
                '10',
                {
                    title: 'Extreme Amateur',
                    standard_description:
                        'Complete 10 extreme demons on the classic or platformer list.',
                },
            ],
            [
                '25',
                {
                    title: 'Extreme Novice',
                    standard_description:
                        'Complete 25 extreme demons on the classic or platformer list.',
                },
            ],
            [
                '50',
                {
                    title: 'Extreme Grinder',
                    standard_description:
                        'Complete 50 extreme demons on the classic or platformer list.',
                },
            ],
            [
                '75',
                {
                    title: 'Extreme Savant',
                    standard_description:
                        'Complete 75 extreme demons on the classic or platformer list.',
                },
            ],
            [
                '100',
                {
                    title: 'Extreme Pro',
                    standard_description:
                        'Complete 100 extreme demons on the classic or platformer list.',
                },
            ],
            [
                '150',
                {
                    title: 'Extreme Veteran',
                    standard_description:
                        'Complete 150 extreme demons on the classic or platformer list.',
                },
            ],
            [
                '200',
                {
                    title: 'Extreme Master',
                    standard_description:
                        'Complete 200 extreme demons on the classic or platformer list.',
                },
            ],
            [
                '250',
                {
                    title: 'Extreme Legend',
                    standard_description:
                        'Complete 250 extreme demons on the classic or platformer list.',
                },
            ],
        ],
    },
    {
        code: 'classic.leaderboard_rank',
        iconIndex: 3,
        tiers: [
            [
                '2000',
                {
                    title: 'Getting Started',
                    standard_description:
                        'Be ranked #2000 or higher on the classic user leaderboard.',
                },
            ],
            [
                '1000',
                {
                    title: 'Climbing The Ladder',
                    standard_description:
                        'Be ranked #1000 or higher on the classic user leaderboard.',
                },
            ],
            [
                '500',
                {
                    title: 'Rising Through The Ranks',
                    standard_description:
                        'Be ranked #500 or higher on the classic user leaderboard.',
                },
            ],
            [
                '250',
                {
                    title: 'Making It',
                    standard_description:
                        'Be ranked #250 or higher on the classic user leaderboard.',
                },
            ],
            [
                '100',
                {
                    title: 'Podium Finish',
                    standard_description:
                        'Be ranked #100 or higher on the classic user leaderboard.',
                },
            ],
            [
                '50',
                {
                    title: 'Big Shot',
                    standard_description:
                        'Be ranked #50 or higher on the classic user leaderboard.',
                },
            ],
            [
                '20',
                {
                    title: 'At The Top',
                    standard_description:
                        'Be ranked #20 or higher on the classic user leaderboard.',
                },
            ],
        ],
    },
    {
        code: 'global.pack_completion',
        iconIndex: 4,
        tiers: [
            [
                '3',
                {
                    title: 'Amazon Delivery',
                    standard_description: 'Complete 3 packs.',
                },
            ],
            [
                '5',
                {
                    title: 'Package Handler',
                    standard_description: 'Complete 5 packs.',
                },
            ],
            [
                '10',
                {
                    title: 'Warehouse Manager',
                    standard_description: 'Complete 10 packs.',
                },
            ],
            [
                '15',
                {
                    title: 'Packgod',
                    standard_description: 'Complete 15 packs.',
                },
            ],
        ],
    },
    {
        code: 'global.hardest_pack_tier',
        iconIndex: 5,
        tiers: [
            [
                'iron',
                {
                    title: 'Iron Golem',
                    standard_description:
                        'Complete a pack in the Iron tier or higher.',
                },
            ],
            [
                'gold',
                {
                    title: 'The Gold Standard',
                    standard_description:
                        'Complete a pack in the Gold tier or higher.',
                },
            ],
            [
                'ruby',
                {
                    title: 'Hidden Gem',
                    standard_description:
                        'Complete a pack in the Ruby tier or higher.',
                },
            ],
            [
                'sapphire',
                {
                    title: 'Blue Velvet',
                    standard_description:
                        'Complete a pack in the Sapphire tier or higher.',
                },
            ],
            [
                'pearl',
                {
                    title: 'Treasure Of The Sea',
                    standard_description:
                        'Complete a pack in the Pearl tier or higher.',
                },
            ],
            [
                'diamond',
                {
                    title: 'Formed Under Pressure',
                    standard_description:
                        'Complete a pack in the Diamond tier.',
                },
            ],
        ],
    },
    {
        code: 'global.bounty_board.weekly',
        iconIndex: 6,
        tiers: [
            [
                '3',
                {
                    title: 'Punching In',
                    standard_description: 'Complete 3 weekly extreme demons.',
                },
            ],
            [
                '7',
                {
                    title: 'Part-Time',
                    standard_description: 'Complete 7 weekly extreme demons.',
                },
            ],
            [
                '12',
                {
                    title: 'Keeping Busy',
                    standard_description: 'Complete 12 weekly extreme demons.',
                },
            ],
            [
                '18',
                {
                    title: 'Routine Check',
                    standard_description: 'Complete 18 weekly extreme demons.',
                },
            ],
            [
                '25',
                {
                    title: 'See You Next Week',
                    standard_description: 'Complete 25 weekly extreme demons.',
                },
            ],
        ],
    },
    {
        code: 'global.bounty_board.monthly',
        iconIndex: 7,
        tiers: [
            [
                '1',
                {
                    title: 'Mission: Possible',
                    standard_description: 'Complete a monthly extreme demon.',
                },
            ],
            [
                '2',
                {
                    title: 'Signed On',
                    standard_description: 'Complete 2 monthly extreme demons.',
                },
            ],
            [
                '4',
                {
                    title: 'Well Established',
                    standard_description: 'Complete 4 monthly extreme demons.',
                },
            ],
            [
                '6',
                {
                    title: 'Committed',
                    standard_description: 'Complete 6 monthly extreme demons.',
                },
            ],
            [
                '8',
                {
                    title: 'Mission: Impossible',
                    standard_description: 'Complete 8 monthly extreme demons.',
                },
            ],
        ],
    },
    {
        code: 'global.bounty_board.bounty',
        iconIndex: 8,
        tiers: [
            [
                '5',
                {
                    title: 'First Contracts',
                    standard_description: 'Complete 5 bounties.',
                },
            ],
            [
                '10',
                {
                    title: 'Getting Paid',
                    standard_description: 'Complete 10 bounties.',
                },
            ],
            [
                '15',
                {
                    title: 'Tracker',
                    standard_description: 'Complete 15 bounties.',
                },
            ],
            [
                '20',
                {
                    title: 'Bounty Hunter',
                    standard_description: 'Complete 20 bounties.',
                },
            ],
            [
                '25',
                {
                    title: 'Most Wanted',
                    standard_description: 'Complete 25 bounties.',
                },
            ],
        ],
    },
    {
        code: 'global.bounty_board.event',
        iconIndex: 9,
        tiers: [
            [
                '1',
                {
                    title: 'All Yours',
                    standard_description: 'Complete an event extreme demon.',
                },
            ],
            [
                '3',
                {
                    title: 'Partygoer',
                    standard_description: 'Complete 3 event extreme demons.',
                },
            ],
            [
                '5',
                {
                    title: 'Headliner',
                    standard_description: 'Complete 5 event extreme demons.',
                },
            ],
        ],
    },
    {
        code: 'global.publisher_levels',
        iconIndex: 10,
        additional_description: (userCustomData: string) =>
            `Awarded for completing ${userCustomData}.`,
        tiers: [
            [
                '4',
                {
                    title: 'Casual Fan',
                    standard_description:
                        'Complete 4 levels by the same publisher.',
                },
            ],
            [
                '8',
                {
                    title: '#1 Fan',
                    standard_description:
                        'Complete 8 levels by the same publisher.',
                },
            ],
        ],
    },
    {
        code: 'global.level_tags.22',
        iconIndex: 11,
        tiers: [
            [
                '5',
                {
                    title: 'Newgen',
                    standard_description:
                        'Complete 5 levels with the "2.2" tag.',
                },
            ],
            [
                '10',
                {
                    title: 'Checked Steam',
                    standard_description:
                        'Complete 10 levels with the "2.2" tag.',
                },
            ],
            [
                '25',
                {
                    title: 'Born Yesterday',
                    standard_description:
                        'Complete 25 levels with the "2.2" tag.',
                },
            ],
            [
                '35',
                {
                    title: 'Starter Pack',
                    standard_description:
                        'Complete 35 levels with the "2.2" tag.',
                },
            ],
            [
                '50',
                {
                    title: 'Late To The Party',
                    standard_description:
                        'Complete 50 levels with the "2.2" tag.',
                },
            ],
        ],
    },
    {
        code: 'global.level_tags.20',
        iconIndex: 12,
        tiers: [
            [
                '5',
                {
                    title: 'Retro',
                    standard_description:
                        'Complete 5 levels with the "2.0" tag.',
                },
            ],
            [
                '10',
                {
                    title: 'On Second Thought',
                    standard_description:
                        'Complete 10 levels with the "2.0" tag.',
                },
            ],
            [
                '15',
                {
                    title: 'Simpler Times',
                    standard_description:
                        'Complete 15 levels with the "2.0" tag.',
                },
            ],
            [
                '20',
                {
                    title: 'Blockbuster',
                    standard_description:
                        'Complete 20 levels with the "2.0" tag.',
                },
            ],
            [
                '30',
                {
                    title: 'Industrial Revolution',
                    standard_description:
                        'Complete 30 levels with the "2.0" tag.',
                },
            ],
        ],
    },
    {
        code: 'global.level_tags.19',
        iconIndex: 13,
        tiers: [
            [
                '5',
                {
                    title: 'Old School',
                    standard_description:
                        'Complete 5 levels with the "1.9" or "1.9PS" tag.',
                },
            ],
            [
                '10',
                {
                    title: 'Throwback',
                    standard_description:
                        'Complete 10 levels with the "1.9" or "1.9PS" tag.',
                },
            ],
            [
                '15',
                {
                    title: 'Antique',
                    standard_description:
                        'Complete 15 levels with the "1.9" or "1.9PS" tag.',
                },
            ],
            [
                '20',
                {
                    title: 'Classical',
                    standard_description:
                        'Complete 20 levels with the "1.9" or "1.9PS" tag.',
                },
            ],
            [
                '30',
                {
                    title: 'The Golden Era',
                    standard_description:
                        'Complete 30 levels with the "1.9" or "1.9PS" tag.',
                },
            ],
        ],
    },
    {
        code: 'global.level_tags.medium',
        iconIndex: 14,
        tiers: [
            [
                '5',
                {
                    title: 'Short And Sweet',
                    standard_description:
                        'Complete 5 levels with the "Medium" tag.',
                },
            ],
            [
                '10',
                {
                    title: 'Snack Sized',
                    standard_description:
                        'Complete 10 levels with the "Medium" tag.',
                },
            ],
            [
                '15',
                {
                    title: 'Speedrunner',
                    standard_description:
                        'Complete 15 levels with the "Medium" tag.',
                },
            ],
            [
                '20',
                {
                    title: 'Attention Deficit',
                    standard_description:
                        'Complete 20 levels with the "Medium" tag.',
                },
            ],
            [
                '30',
                {
                    title: 'No Patience',
                    standard_description:
                        'Complete 30 levels with the "Medium" tag.',
                },
            ],
        ],
    },
    {
        code: 'global.level_tags.xl',
        iconIndex: 15,
        tiers: [
            [
                '5',
                {
                    title: 'Walk In The Park',
                    standard_description:
                        'Complete 5 levels with the "XL" tag.',
                },
            ],
            [
                '10',
                {
                    title: 'Evening Stroll',
                    standard_description:
                        'Complete 10 levels with the "XL" tag.',
                },
            ],
            [
                '25',
                {
                    title: 'Casual Job',
                    standard_description:
                        'Complete 25 levels with the "XL" tag.',
                },
            ],
            [
                '35',
                {
                    title: 'Steady Runner',
                    standard_description:
                        'Complete 35 levels with the "XL" tag.',
                },
            ],
            [
                '50',
                {
                    title: 'Marathon Medalist',
                    standard_description:
                        'Complete 50 levels with the "XL" tag.',
                },
            ],
        ],
    },
    {
        code: 'global.level_tags.xxl',
        iconIndex: 16,
        tiers: [
            [
                '5',
                {
                    title: 'Road Trip',
                    standard_description:
                        'Complete 5 levels with the "XXL" tag.',
                },
            ],
            [
                '10',
                {
                    title: 'The Long Haul',
                    standard_description:
                        'Complete 10 levels with the "XXL" tag.',
                },
            ],
            [
                '25',
                {
                    title: 'No End In Sight',
                    standard_description:
                        'Complete 25 levels with the "XXL" tag.',
                },
            ],
            [
                '35',
                {
                    title: 'An Endless Journey',
                    standard_description:
                        'Complete 35 levels with the "XXL" tag.',
                },
            ],
            [
                '50',
                {
                    title: 'One Eternity Later',
                    standard_description:
                        'Complete 50 levels with the "XXL" tag.',
                },
            ],
        ],
    },
    {
        code: 'global.level_tags.xxlplus',
        iconIndex: 17,
        tiers: [
            [
                '3',
                {
                    title: 'Marathon',
                    standard_description:
                        'Complete 3 levels with the "XXL+" tag.',
                },
            ],
            [
                '5',
                {
                    title: 'Insomniac',
                    standard_description:
                        'Complete 5 levels with the "XXL+" tag.',
                },
            ],
            [
                '10',
                {
                    title: 'Non-Stop',
                    standard_description:
                        'Complete 10 levels with the "XXL+" tag.',
                },
            ],
            [
                '15',
                {
                    title: '24/7',
                    standard_description:
                        'Complete 15 levels with the "XXL+" tag.',
                },
            ],
            [
                '20',
                {
                    title: 'Full Time Job',
                    standard_description:
                        'Complete 20 levels with the "XXL+" tag.',
                },
            ],
        ],
    },
    {
        code: 'global.level_tags.nong',
        iconIndex: 18,
        tiers: [
            [
                '5',
                {
                    title: 'Remix',
                    standard_description:
                        'Complete 5 levels with the "NONG" tag.',
                },
            ],
            [
                '10',
                {
                    title: 'Playlist',
                    standard_description:
                        'Complete 10 levels with the "NONG" tag.',
                },
            ],
            [
                '15',
                {
                    title: 'Stereo Mix',
                    standard_description:
                        'Complete 15 levels with the "NONG" tag.',
                },
            ],
            [
                '20',
                {
                    title: 'Boombox',
                    standard_description:
                        'Complete 20 levels with the "NONG" tag.',
                },
            ],
            [
                '25',
                {
                    title: 'DJ',
                    standard_description:
                        'Complete 25 levels with the "NONG" tag.',
                },
            ],
        ],
    },
    {
        code: 'global.level_tags.2p',
        iconIndex: 19,
        tiers: [
            [
                '3',
                {
                    title: 'Teamwork',
                    standard_description:
                        'Complete 3 levels with the "2P" tag.',
                },
            ],
            [
                '5',
                {
                    title: '2 For 1',
                    standard_description:
                        'Complete 5 levels with the "2P" tag.',
                },
            ],
            [
                '10',
                {
                    title: 'Codependent',
                    standard_description:
                        'Complete 10 levels with the "2P" tag.',
                },
            ],
            [
                '15',
                {
                    title: 'Dynamic Duo',
                    standard_description:
                        'Complete 15 levels with the "2P" tag.',
                },
            ],
            [
                '20',
                {
                    title: 'Package Deal',
                    standard_description:
                        'Complete 20 levels with the "2P" tag.',
                },
            ],
        ],
    },
    {
        code: 'global.level_tags.circles',
        iconIndex: 20,
        tiers: [
            [
                '5',
                {
                    title: 'Nine What?',
                    standard_description:
                        'Complete 5 levels with the "Circles" tag.',
                },
            ],
            [
                '10',
                {
                    title: 'My Eyes Are Fine',
                    standard_description:
                        'Complete 10 levels with the "Circles" tag.',
                },
            ],
            [
                '15',
                {
                    title: 'In The Vortex',
                    standard_description:
                        'Complete 15 levels with the "Circles" tag.',
                },
            ],
            [
                '20',
                {
                    title: 'In Circles',
                    standard_description:
                        'Complete 20 levels with the "Circles" tag.',
                },
            ],
            [
                '30',
                {
                    title: 'Dante',
                    standard_description:
                        'Complete 30 levels with the "Circles" tag.',
                },
            ],
        ],
    },
    {
        code: 'global.level_tags.clicksync',
        iconIndex: 21,
        tiers: [
            [
                '5',
                {
                    title: 'Playing It By Ear',
                    standard_description:
                        'Complete 5 levels with the "Clicksync" tag.',
                },
            ],
            [
                '10',
                {
                    title: 'Rhythmic',
                    standard_description:
                        'Complete 10 levels with the "Clicksync" tag.',
                },
            ],
            [
                '25',
                {
                    title: 'Feeling It',
                    standard_description:
                        'Complete 25 levels with the "Clicksync" tag.',
                },
            ],
            [
                '35',
                {
                    title: 'Noteworthy',
                    standard_description:
                        'Complete 35 levels with the "Clicksync" tag.',
                },
            ],
            [
                '50',
                {
                    title: 'Metronome',
                    standard_description:
                        'Complete 50 levels with the "Clicksync" tag.',
                },
            ],
        ],
    },
    {
        code: 'global.level_tags.fastpaced',
        iconIndex: 22,
        tiers: [
            [
                '5',
                {
                    title: 'Speedy',
                    standard_description:
                        'Complete 5 levels with the "Fast-Paced" tag.',
                },
            ],
            [
                '10',
                {
                    title: 'Accelerated',
                    standard_description:
                        'Complete 10 levels with the "Fast-Paced" tag.',
                },
            ],
            [
                '25',
                {
                    title: 'Fast And Furious',
                    standard_description:
                        'Complete 25 levels with the "Fast-Paced" tag.',
                },
            ],
            [
                '35',
                {
                    title: 'Express Lane',
                    standard_description:
                        'Complete 35 levels with the "Fast-Paced" tag.',
                },
            ],
            [
                '50',
                {
                    title: 'Speed Demon',
                    standard_description:
                        'Complete 50 levels with the "Fast-Paced" tag.',
                },
            ],
        ],
    },
    {
        code: 'global.level_tags.timings',
        iconIndex: 23,
        tiers: [
            [
                '5',
                {
                    title: 'Punctual',
                    standard_description:
                        'Complete 5 levels with the "Timings" tag.',
                },
            ],
            [
                '10',
                {
                    title: 'Right On Time',
                    standard_description:
                        'Complete 10 levels with the "Timings" tag.',
                },
            ],
            [
                '25',
                {
                    title: '99% Accuracy',
                    standard_description:
                        'Complete 25 levels with the "Timings" tag.',
                },
            ],
            [
                '35',
                {
                    title: 'Flawless',
                    standard_description:
                        'Complete 35 levels with the "Timings" tag.',
                },
            ],
            [
                '50',
                {
                    title: 'Frame Perfect',
                    standard_description:
                        'Complete 50 levels with the "Timings" tag.',
                },
            ],
        ],
    },
    {
        code: 'global.level_tags.chokepoints',
        iconIndex: 24,
        tiers: [
            [
                '5',
                {
                    title: 'Blockage',
                    standard_description:
                        'Complete 5 levels with the "Chokepoints" tag.',
                },
            ],
            [
                '10',
                {
                    title: 'Traffic Jam',
                    standard_description:
                        'Complete 10 levels with the "Chokepoints" tag.',
                },
            ],
            [
                '25',
                {
                    title: 'Bottlenecked',
                    standard_description:
                        'Complete 25 levels with the "Chokepoints" tag.',
                },
            ],
            [
                '35',
                {
                    title: 'Gridlocked',
                    standard_description:
                        'Complete 35 levels with the "Chokepoints" tag.',
                },
            ],
            [
                '50',
                {
                    title: 'Heimlich Maneuver',
                    standard_description:
                        'Complete 50 levels with the "Chokepoints" tag.',
                },
            ],
        ],
    },
    {
        code: 'global.level_tags.learny',
        iconIndex: 25,
        tiers: [
            [
                '5',
                {
                    title: 'A Learning Experience',
                    standard_description:
                        'Complete 5 levels with the "Learny" tag.',
                },
            ],
            [
                '10',
                {
                    title: 'Practice Mode',
                    standard_description:
                        'Complete 10 levels with the "Learny" tag.',
                },
            ],
            [
                '25',
                {
                    title: 'Studious',
                    standard_description:
                        'Complete 25 levels with the "Learny" tag.',
                },
            ],
            [
                '35',
                {
                    title: 'Scholarly',
                    standard_description:
                        'Complete 35 levels with the "Learny" tag.',
                },
            ],
            [
                '50',
                {
                    title: 'PhD',
                    standard_description:
                        'Complete 50 levels with the "Learny" tag.',
                },
            ],
        ],
    },
    {
        code: 'global.level_tags.memory',
        iconIndex: 26,
        tiers: [
            [
                '5',
                {
                    title: 'As A Reminder',
                    standard_description:
                        'Complete 5 levels with the "Memory" tag.',
                },
            ],
            [
                '10',
                {
                    title: 'Those Who Know',
                    standard_description:
                        'Complete 10 levels with the "Memory" tag.',
                },
            ],
            [
                '25',
                {
                    title: 'Focused',
                    standard_description:
                        'Complete 25 levels with the "Memory" tag.',
                },
            ],
            [
                '35',
                {
                    title: 'Trapped In Limbo',
                    standard_description:
                        'Complete 35 levels with the "Memory" tag.',
                },
            ],
            [
                '50',
                {
                    title: 'Mastermind',
                    standard_description:
                        'Complete 50 levels with the "Memory" tag.',
                },
            ],
        ],
    },
    {
        code: 'global.level_tags.highcps',
        iconIndex: 27,
        tiers: [
            [
                '5',
                {
                    title: 'Rapid Fire',
                    standard_description:
                        'Complete 5 levels with the "High CPS" tag.',
                },
            ],
            [
                '10',
                {
                    title: 'Alternator',
                    standard_description:
                        'Complete 10 levels with the "High CPS" tag.',
                },
            ],
            [
                '25',
                {
                    title: 'Telly Bridger',
                    standard_description:
                        'Complete 25 levels with the "High CPS" tag.',
                },
            ],
            [
                '35',
                {
                    title: 'Carpal Tunnel',
                    standard_description:
                        'Complete 35 levels with the "High CPS" tag.',
                },
            ],
            [
                '50',
                {
                    title: 'Total Whiz',
                    standard_description:
                        'Complete 50 levels with the "High CPS" tag.',
                },
            ],
        ],
    },
    {
        code: 'global.level_tags.gimmicky',
        iconIndex: 28,
        tiers: [
            [
                '5',
                {
                    title: 'Discovery',
                    standard_description:
                        'Complete 5 levels with the "Gimmicky" tag.',
                },
            ],
            [
                '10',
                {
                    title: 'Invention',
                    standard_description:
                        'Complete 10 levels with the "Gimmicky" tag.',
                },
            ],
            [
                '15',
                {
                    title: 'Innovation',
                    standard_description:
                        'Complete 15 levels with the "Gimmicky" tag.',
                },
            ],
            [
                '20',
                {
                    title: 'Breakthrough',
                    standard_description:
                        'Complete 20 levels with the "Gimmicky" tag.',
                },
            ],
            [
                '25',
                {
                    title: 'Paradigm Shift',
                    standard_description:
                        'Complete 25 levels with the "Gimmicky" tag.',
                },
            ],
        ],
    },
    {
        code: 'global.level_tags.flow',
        iconIndex: 29,
        tiers: [
            [
                '5',
                {
                    title: 'The Rapids',
                    standard_description:
                        'Complete 5 levels with the "Flow" tag.',
                },
            ],
            [
                '10',
                {
                    title: 'Streamlined',
                    standard_description:
                        'Complete 10 levels with the "Flow" tag.',
                },
            ],
            [
                '25',
                {
                    title: 'Like A River',
                    standard_description:
                        'Complete 25 levels with the "Flow" tag.',
                },
            ],
            [
                '35',
                {
                    title: 'Raging Water',
                    standard_description:
                        'Complete 35 levels with the "Flow" tag.',
                },
            ],
            [
                '50',
                {
                    title: 'The Flood',
                    standard_description:
                        'Complete 50 levels with the "Flow" tag.',
                },
            ],
        ],
    },
    {
        code: 'global.level_tags.slowpaced',
        iconIndex: 30,
        tiers: [
            [
                '5',
                {
                    title: 'Decelerating',
                    standard_description:
                        'Complete 5 levels with the "Slow-Paced" tag.',
                },
            ],
            [
                '10',
                {
                    title: 'Taking It Easy',
                    standard_description:
                        'Complete 10 levels with the "Slow-Paced" tag.',
                },
            ],
            [
                '25',
                {
                    title: 'No Rush',
                    standard_description:
                        'Complete 25 levels with the "Slow-Paced" tag.',
                },
            ],
            [
                '35',
                {
                    title: 'Slowpoke',
                    standard_description:
                        'Complete 35 levels with the "Slow-Paced" tag.',
                },
            ],
            [
                '50',
                {
                    title: 'True Tranquility',
                    standard_description:
                        'Complete 50 levels with the "Slow-Paced" tag.',
                },
            ],
        ],
    },
    {
        code: 'global.level_tags.bossfight',
        iconIndex: 31,
        tiers: [
            [
                '3',
                {
                    title: 'Warrior',
                    standard_description:
                        'Complete 3 levels with the "Bossfight" tag.',
                },
            ],
            [
                '5',
                {
                    title: 'Gladiator',
                    standard_description:
                        'Complete 5 levels with the "Bossfight" tag.',
                },
            ],
            [
                '10',
                {
                    title: 'Duelist',
                    standard_description:
                        'Complete 10 levels with the "Bossfight" tag.',
                },
            ],
            [
                '15',
                {
                    title: 'Monster Hunter',
                    standard_description:
                        'Complete 15 levels with the "Bossfight" tag.',
                },
            ],
            [
                '20',
                {
                    title: 'Champion Of The Colosseum',
                    standard_description:
                        'Complete 20 levels with the "Bossfight" tag.',
                },
            ],
        ],
    },
    {
        code: 'global.level_tags.mirror',
        iconIndex: 32,
        tiers: [
            [
                '3',
                {
                    title: 'On The Flip Side',
                    standard_description:
                        'Complete 3 levels with the "Mirror" tag.',
                },
            ],
            [
                '5',
                {
                    title: 'Reflective',
                    standard_description:
                        'Complete 5 levels with the "Mirror" tag.',
                },
            ],
            [
                '10',
                {
                    title: 'Dizzying',
                    standard_description:
                        'Complete 10 levels with the "Mirror" tag.',
                },
            ],
            [
                '15',
                {
                    title: 'Two-Faced',
                    standard_description:
                        'Complete 15 levels with the "Mirror" tag.',
                },
            ],
            [
                '20',
                {
                    title: 'Take Me Back',
                    standard_description:
                        'Complete 20 levels with the "Mirror" tag.',
                },
            ],
        ],
    },
    {
        code: 'global.level_tags.nervecontrol',
        iconIndex: 33,
        tiers: [
            [
                '5',
                {
                    title: 'Steady Hands',
                    standard_description:
                        'Complete 5 levels with the "Nerve Control" tag.',
                },
            ],
            [
                '10',
                {
                    title: 'Nerves Of Steel',
                    standard_description:
                        'Complete 10 levels with the "Nerve Control" tag.',
                },
            ],
            [
                '25',
                {
                    title: 'Cool As Ice',
                    standard_description:
                        'Complete 25 levels with the "Nerve Control" tag.',
                },
            ],
            [
                '35',
                {
                    title: 'No Sweat',
                    standard_description:
                        'Complete 35 levels with the "Nerve Control" tag.',
                },
            ],
            [
                '50',
                {
                    title: 'Ultimate Serenity',
                    standard_description:
                        'Complete 50 levels with the "Nerve Control" tag.',
                },
            ],
        ],
    },
    {
        code: 'global.level_tags.cube',
        iconIndex: 34,
        tiers: [
            [
                '5',
                {
                    title: 'Square One',
                    standard_description:
                        'Complete 5 levels with the "Cube" tag.',
                },
            ],
            [
                '10',
                {
                    title: 'Power Of Two',
                    standard_description:
                        'Complete 10 levels with the "Cube" tag.',
                },
            ],
            [
                '15',
                {
                    title: 'Power Of Three',
                    standard_description:
                        'Complete 15 levels with the "Cube" tag.',
                },
            ],
            [
                '20',
                {
                    title: 'Hypercubical',
                    standard_description:
                        'Complete 20 levels with the "Cube" tag.',
                },
            ],
            [
                '25',
                {
                    title: 'The Impossible Game',
                    standard_description:
                        'Complete 25 levels with the "Cube" tag.',
                },
            ],
        ],
    },
    {
        code: 'global.level_tags.ship',
        iconIndex: 35,
        tiers: [
            [
                '5',
                {
                    title: 'Sailor',
                    standard_description:
                        'Complete 5 levels with the "Ship" tag.',
                },
            ],
            [
                '10',
                {
                    title: 'Navigator',
                    standard_description:
                        'Complete 10 levels with the "Ship" tag.',
                },
            ],
            [
                '25',
                {
                    title: 'Lieutenant',
                    standard_description:
                        'Complete 25 levels with the "Ship" tag.',
                },
            ],
            [
                '35',
                {
                    title: 'Captain',
                    standard_description:
                        'Complete 35 levels with the "Ship" tag.',
                },
            ],
            [
                '50',
                {
                    title: 'Admiral',
                    standard_description:
                        'Complete 50 levels with the "Ship" tag.',
                },
            ],
        ],
    },
    {
        code: 'global.level_tags.ball',
        iconIndex: 36,
        tiers: [
            [
                '3',
                {
                    title: 'Bowling',
                    standard_description:
                        'Complete 3 levels with the "Ball" tag.',
                },
            ],
            [
                '5',
                {
                    title: 'Bouncy',
                    standard_description:
                        'Complete 5 levels with the "Ball" tag.',
                },
            ],
            [
                '10',
                {
                    title: 'Ball Pit',
                    standard_description:
                        'Complete 10 levels with the "Ball" tag.',
                },
            ],
            [
                '15',
                {
                    title: 'Ball Knowledge',
                    standard_description:
                        'Complete 15 levels with the "Ball" tag.',
                },
            ],
            [
                '20',
                {
                    title: 'Baller',
                    standard_description:
                        'Complete 20 levels with the "Ball" tag.',
                },
            ],
        ],
    },
    {
        code: 'global.level_tags.ufo',
        iconIndex: 37,
        tiers: [
            [
                '3',
                {
                    title: 'Extraterrestrial',
                    standard_description:
                        'Complete 3 levels with the "UFO" tag.',
                },
            ],
            [
                '5',
                {
                    title: 'Area 51',
                    standard_description:
                        'Complete 5 levels with the "UFO" tag.',
                },
            ],
            [
                '10',
                {
                    title: 'Flappy Bird',
                    standard_description:
                        'Complete 10 levels with the "UFO" tag.',
                },
            ],
            [
                '15',
                {
                    title: 'Abducted',
                    standard_description:
                        'Complete 15 levels with the "UFO" tag.',
                },
            ],
            [
                '20',
                {
                    title: 'Alien Invasion',
                    standard_description:
                        'Complete 20 levels with the "UFO" tag.',
                },
            ],
        ],
    },
    {
        code: 'global.level_tags.wave',
        iconIndex: 38,
        tiers: [
            [
                '5',
                {
                    title: 'Cruising',
                    standard_description:
                        'Complete 5 levels with the "Wave" tag.',
                },
            ],
            [
                '10',
                {
                    title: 'Silver Surfer',
                    standard_description:
                        'Complete 10 levels with the "Wave" tag.',
                },
            ],
            [
                '25',
                {
                    title: 'Zigzag Trials',
                    standard_description:
                        'Complete 25 levels with the "Wave" tag.',
                },
            ],
            [
                '35',
                {
                    title: 'Waving With Npesta',
                    standard_description:
                        'Complete 35 levels with the "Wave" tag.',
                },
            ],
            [
                '50',
                {
                    title: 'Wave Carried',
                    standard_description:
                        'Complete 50 levels with the "Wave" tag.',
                },
            ],
        ],
    },
    {
        code: 'global.level_tags.robot',
        iconIndex: 39,
        tiers: [
            [
                '3',
                {
                    title: 'Digital',
                    standard_description:
                        'Complete 3 levels with the "Robot" tag.',
                },
            ],
            [
                '5',
                {
                    title: 'Cybernetic',
                    standard_description:
                        'Complete 5 levels with the "Robot" tag.',
                },
            ],
            [
                '10',
                {
                    title: 'Machine Learning',
                    standard_description:
                        'Complete 10 levels with the "Robot" tag.',
                },
            ],
            [
                '15',
                {
                    title: 'Neural Network',
                    standard_description:
                        'Complete 15 levels with the "Robot" tag.',
                },
            ],
            [
                '20',
                {
                    title: 'Artificial Intelligence',
                    standard_description:
                        'Complete 20 levels with the "Robot" tag.',
                },
            ],
        ],
    },
    {
        code: 'global.level_tags.spider',
        iconIndex: 40,
        tiers: [
            [
                '3',
                {
                    title: 'Crawling',
                    standard_description:
                        'Complete 3 levels with the "Spider" tag.',
                },
            ],
            [
                '5',
                {
                    title: 'The Outback',
                    standard_description:
                        'Complete 5 levels with the "Spider" tag.',
                },
            ],
            [
                '10',
                {
                    title: 'Australian',
                    standard_description:
                        'Complete 10 levels with the "Spider" tag.',
                },
            ],
            [
                '15',
                {
                    title: 'Funnel Web',
                    standard_description:
                        'Complete 15 levels with the "Spider" tag.',
                },
            ],
            [
                '20',
                {
                    title: 'Arachnophilia',
                    standard_description:
                        'Complete 20 levels with the "Spider" tag.',
                },
            ],
        ],
    },
    {
        code: 'global.level_tags.oldswing',
        iconIndex: 41,
        tiers: [
            [
                '5',
                {
                    title: 'An Ancient Invention',
                    standard_description:
                        'Complete 5 levels with the "Old Swing" tag.',
                },
            ],
            [
                '10',
                {
                    title: 'Swing And A Miss',
                    standard_description:
                        'Complete 10 levels with the "Old Swing" tag.',
                },
            ],
            [
                '15',
                {
                    title: 'Home Run',
                    standard_description:
                        'Complete 15 levels with the "Old Swing" tag.',
                },
            ],
        ],
    },
    {
        code: 'global.level_tags.newswing',
        iconIndex: 42,
        tiers: [
            [
                '3',
                {
                    title: 'Flip Flop',
                    standard_description:
                        'Complete 3 levels with the "New Swing" tag.',
                },
            ],
            [
                '5',
                {
                    title: 'Playground',
                    standard_description:
                        'Complete 5 levels with the "New Swing" tag.',
                },
            ],
            [
                '10',
                {
                    title: 'Switched Up',
                    standard_description:
                        'Complete 10 levels with the "New Swing" tag.',
                },
            ],
        ],
    },
    {
        code: 'global.level_tags.duals',
        iconIndex: 43,
        tiers: [
            [
                '5',
                {
                    title: 'Double Life',
                    standard_description:
                        'Complete 5 levels with the "Duals" tag.',
                },
            ],
            [
                '10',
                {
                    title: 'Double Down',
                    standard_description:
                        'Complete 10 levels with the "Duals" tag.',
                },
            ],
            [
                '15',
                {
                    title: 'Double Trouble',
                    standard_description:
                        'Complete 15 levels with the "Duals" tag.',
                },
            ],
            [
                '20',
                {
                    title: 'Double The Fun',
                    standard_description:
                        'Complete 20 levels with the "Duals" tag.',
                },
            ],
            [
                '25',
                {
                    title: 'Double Dasher',
                    standard_description:
                        'Complete 25 levels with the "Duals" tag.',
                },
            ],
        ],
    },
    {
        code: 'global.level_tags.overall',
        iconIndex: 44,
        tiers: [
            [
                '5',
                {
                    title: 'Well Rounded',
                    standard_description:
                        'Complete 5 levels with the "Overall" tag.',
                },
            ],
            [
                '10',
                {
                    title: 'Versatile',
                    standard_description:
                        'Complete 10 levels with the "Overall" tag.',
                },
            ],
            [
                '25',
                {
                    title: 'Jack Of All Trades',
                    standard_description:
                        'Complete 25 levels with the "Overall" tag.',
                },
            ],
            [
                '35',
                {
                    title: 'Ruler Of Everything',
                    standard_description:
                        'Complete 35 levels with the "Overall" tag.',
                },
            ],
            [
                '50',
                {
                    title: 'True Balance',
                    standard_description:
                        'Complete 50 levels with the "Overall" tag.',
                },
            ],
        ],
    },
];
export const singleBadges: SingleBadgeDescription[] = [
    {
        code: 'global.level_tags.alltags.1',
        iconIndex: 45,
        title: 'Tagged Out',
        standard_description:
            'Complete at least one level for every tag (excluding version and length tags).',
    },
    {
        code: 'global.alphabet',
        iconIndex: 46,
        title: 'Alphabetical',
        standard_description:
            'Complete at least one level starting with every letter of the alphabet.',
    },
    {
        code: 'global.first_victor',
        iconIndex: 47,
        title: 'First Victor',
        standard_description:
            'Be the first player to complete a level on the AREDL.',
        additional_description: (userCustomData: string) =>
            `Awarded for being the first AREDL victor of ${userCustomData}.`,
    },
    {
        code: 'global.creator',
        iconIndex: 48,
        title: 'Creator',
        standard_description:
            'Be credited as a creator or a publisher of a level on the AREDL.',
        additional_description: (userCustomData: string) =>
            `Awarded for creating/publishing ${userCustomData}.`,
    },
    {
        code: 'global.verifier',
        iconIndex: 49,
        title: 'Verifier',
        standard_description:
            'Be credited as the verifier of a level on the AREDL.',
        additional_description: (userCustomData: string) =>
            `Awarded for verifying ${userCustomData}.`,
    },
    {
        code: 'global.all_nlw',
        iconIndex: 50,
        title: 'Equilibrium',
        standard_description: 'Complete one level of each NLW tier.',
    },
    {
        code: 'global.edel_high',
        iconIndex: 51,
        title: 'Fan Favorite',
        standard_description:
            'Complete 3 levels with an EDEL rating of 90 or higher.',
    },
    {
        code: 'global.edel_low',
        iconIndex: 52,
        title: 'Never Again',
        standard_description:
            'Complete 3 levels with an EDEL rating of 40 or lower.',
    },
    {
        code: 'global.2p_and_solo',
        iconIndex: 53,
        title: 'Ambidextrous',
        standard_description:
            'Complete a two-player level on both the solo and 2P versions.',
    },
    {
        code: 'platformer.fastest_time',
        iconIndex: 54,
        title: 'World Record',
        standard_description:
            'Get the fastest time on a platformer extreme demon.',
    },
];

export type BadgesMap = Map<string, BadgeDescription & BadgeIconPosition>;

export const allBadgesMap: BadgesMap = new Map([
    ...singleBadges.reduce((acc, { code, ...badge }) => {
        acc.set(code, { ...badge, iconColumn: 0 });
        return acc;
    }, new Map() as BadgesMap),
    ...tieredBadges
        .flatMap((tieredBadge) =>
            tieredBadge.tiers.map(([level, description], iconColumn) => ({
                code: `${tieredBadge.code}.${level}`,
                iconIndex: tieredBadge.iconIndex,
                iconColumn,
                additional_description: tieredBadge.additional_description,
                ...description,
            })),
        )
        .reduce((acc, { code, ...description }) => {
            acc.set(code, description);
            return acc;
        }, new Map() as BadgesMap),
]);

export type FullBadgeDescription = BadgeDescription &
    BadgeIconPosition &
    Partial<Omit<Badge, 'badge_code'>>;

export type FullBadgeData = BadgeDescription &
    BadgeIconPosition &
    Partial<Badge> &
    Pick<Badge, 'badge_code'>;
